import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/** How long a confirmation stays on screen. */
const VISIBLE_MS = 2500;

/** Rapid taps stack up to this many; older ones make way. */
const MAX_VISIBLE = 3;

/**
 * Confirms wishlist changes with a short toast (snippets/wishlist-toast.liquid).
 *
 * Listens for the wishlist's own update event rather than being called by each heart, so every
 * way of changing the wishlist — card, product page, drawer — confirms the same way. The page-load
 * `hydrate` event is not a change and shows nothing.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} stack - The live region the toasts are added to.
 * @property {HTMLTemplateElement} template - One toast.
 *
 * @extends {Component<Refs>}
 */
class WishlistToast extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.#onUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.#onUpdate);
  }

  /** @param {Event & { detail?: { action?: string } }} event */
  #onUpdate = (event) => {
    const { action } = event.detail ?? {};
    const message = action === 'add' ? this.dataset.messageAdd : action === 'remove' ? this.dataset.messageRemove : '';
    if (message) this.#show(message);
  };

  /** @param {string} message */
  #show(message) {
    const { stack, template } = this.refs;
    const toast = template.content.firstElementChild?.cloneNode(true);
    if (!(toast instanceof HTMLElement)) return;

    const text = toast.querySelector('[data-message]');
    if (text) text.textContent = message;

    stack.append(toast);
    while (stack.children.length > MAX_VISIBLE) stack.firstElementChild?.remove();

    setTimeout(() => this.#dismiss(toast), VISIBLE_MS);
  }

  /** @param {HTMLElement} toast */
  #dismiss(toast) {
    if (!toast.isConnected) return;
    toast.dataset.leaving = '';
    toast.addEventListener('animationend', () => toast.remove(), { once: true });
  }
}

if (!customElements.get('wishlist-toast')) {
  customElements.define('wishlist-toast', WishlistToast);
}
